type AssessedItemRef = { id: string; title: null | string | undefined };

interface CollectAssessedItemTitlesOptions {
  /**
   * Drop items whose resolved id has already been emitted. A risk rated both
   * inherent and residual produces separate controlled + uncontrolled result
   * rows pointing at the same risk, so without this it is listed twice.
   */
  dedupe?: boolean;
  /**
   * Sort titles alphabetically within each accessor group. The underlying
   * query returns rows in arbitrary order, so without this the joined string
   * is non-deterministic between loads.
   */
  sort?: boolean;
}

/**
 * Build the comma-separated "Assessed items" cell value for an assessment
 * register row. Each accessor resolves one assessed-item type from a result
 * row (returning its id + title, or a falsy value when absent); groups are
 * emitted in accessor order to keep the column's type grouping stable.
 */
export const collectAssessedItemTitles = <T>(
  assessedItems: T[],
  accessors: ((item: T) => AssessedItemRef | null | undefined)[],
  options: CollectAssessedItemTitlesOptions = {}
): string => {
  const seenIds = new Set<string>();

  return accessors
    .flatMap((accessor) => {
      const group = assessedItems
        .map(accessor)
        .filter((ref): ref is { id: string; title: string } =>
          Boolean(ref?.title)
        );

      return options.sort
        ? [...group].sort((a, b) => a.title.localeCompare(b.title))
        : group;
    })
    .filter(({ id }) => {
      if (!options.dedupe) {
        return true;
      }
      if (seenIds.has(id)) {
        return false;
      }
      seenIds.add(id);

      return true;
    })
    .map(({ title }) => title)
    .join(', ');
};
